import { AlertBanner } from '@/components/common/AlertBanner'
import { Modal, SaveIndicator, TextareaInput, Toggle } from '@/components/common/Modal'
import { useAutosave } from '@/components/common/useAutosave'
import type { ClinicalSummaryData } from '@/components/order/OrderTypes'
import {
  CLINICAL_DESCRIPTION_MAX_LENGTH,
  type ClinicalTextBudget,
  combinedClinicalDescriptionLength,
  isValidFreeText,
  RCIGM_COMBINED_CLINICAL_TEXT_BUDGET,
  SHARED_CLINICAL_TEXT_HELPER,
  SHARED_CLINICAL_TEXT_TOO_LONG_MESSAGE,
} from '@/utils/freeTextValidation'
import { ArrowRight } from 'flowbite-react-icons/outline'
import { useEffect, useRef, useState } from 'react'

interface ClinicalSummaryModalProps {
  isOpen: boolean
  onClose: () => void
  initialData: ClinicalSummaryData
  onSave: (data: ClinicalSummaryData) => void
  onDone?: (data: ClinicalSummaryData) => void
  autoUpdatePhenotypes?: boolean
  onAutoUpdatePhenotypesChange?: (value: boolean) => void
  /**
   * R5: when provided, renders the brand "Continue to Indications for Testing"
   * footer CTA. The parent persists the summary and advances to the Indications
   * modal; "Done" demotes to the secondary style so there is a single primary.
   *
   * May return a promise — when it does (e.g. it awaits async HPO extraction
   * before opening the Indications modal), this modal stays open and shows an
   * "Extracting…" state until it resolves, so the next modal hydrates with the
   * freshly extracted phenotypes instead of stale ones.
   */
  onContinue?: (data: ClinicalSummaryData) => void | Promise<void>
  /**
   * Current family-history free text from canonical. Included in the combined clinical
   * `description` budget so the counter reflects what provider-api will emit to the lab.
   */
  familyHistory?: string
  /**
   * Rady publishes these fields as one CareBus description capped at 10,000 characters;
   * other labs share one order-wide pool with every comparator summary.
   */
  clinicalTextBudget?: ClinicalTextBudget
}

export function ClinicalSummaryModal({
  isOpen,
  onClose,
  initialData,
  onSave,
  onDone,
  autoUpdatePhenotypes,
  onAutoUpdatePhenotypesChange,
  onContinue,
  familyHistory,
  clinicalTextBudget = RCIGM_COMBINED_CLINICAL_TEXT_BUDGET,
}: ClinicalSummaryModalProps) {
  const [data, setData] = useState<ClinicalSummaryData>(initialData)
  const [showSummaryTip, setShowSummaryTip] = useState(true)
  // True while `onContinue` is awaiting (e.g. async HPO extraction). Keeps the
  // modal open and the footer disabled so the next modal opens with fresh data.
  const [isContinuing, setIsContinuing] = useState(false)
  // R5: the Differential Diagnosis and Prior Genetic Testing fields are opt-in
  // behind a single toggle. Default it on when the order already carries content
  // in either field so existing data is never hidden.
  const [includeDifferential, setIncludeDifferential] = useState(
    Boolean(initialData.differentialDiagnosis?.trim() || initialData.priorGeneticTesting?.trim())
  )
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const { lastSaveTime, isSaving } = useAutosave(data, onSave, 500, isOpen)

  // Keep latest `initialData` on a ref so the open-hydration effect doesn't
  // fight autosave-driven canonical updates that change `initialData`
  // identity on every keystroke. Capture an open-time baseline so Discard
  // reverts to the pre-session value rather than the last autosaved one.
  const initialDataRef = useRef(initialData)
  initialDataRef.current = initialData
  const openBaselineRef = useRef<ClinicalSummaryData>(initialData)

  useEffect(() => {
    if (isOpen) {
      openBaselineRef.current = initialDataRef.current
      setData(initialDataRef.current)
      setShowSummaryTip(true)
      setIsContinuing(false)
      setIncludeDifferential(
        Boolean(
          initialDataRef.current.differentialDiagnosis?.trim() ||
            initialDataRef.current.priorGeneticTesting?.trim()
        )
      )
      setTimeout(() => {
        textareaRef.current?.focus()
      }, 100)
    }
  }, [isOpen])

  const summaryFreeTextValid = isValidFreeText(data.summary)
  const differentialFreeTextValid = isValidFreeText(data.differentialDiagnosis ?? '')
  const priorGeneticFreeTextValid = isValidFreeText(data.priorGeneticTesting ?? '')
  const combinedLength =
    combinedClinicalDescriptionLength(
      data.summary,
      familyHistory ?? '',
      data.differentialDiagnosis ?? '',
      data.priorGeneticTesting ?? ''
    ) + clinicalTextBudget.usedElsewhere
  const overCombinedLimit = combinedLength > clinicalTextBudget.limit
  const isShared = clinicalTextBudget.kind === 'shared'
  const counterProps = {
    characterCount: combinedLength,
    characterLimit: clinicalTextBudget.limit,
    characterCountOverLimit: overCombinedLimit,
  }
  const sectionCounterHelper = isShared
    ? SHARED_CLINICAL_TEXT_HELPER
    : 'The counter reflects the combined clinical summary, differential diagnosis, prior genetic testing, and family history.'
  // Content-validity gate shared by the "Done" CTA and save-and-close (esc/X).
  // Excludes `isSaving` on purpose: a mid-flight autosave should not silently
  // skip finalization/extraction on close — only genuinely invalid or
  // over-limit content should.
  const finalizeDisabled =
    !summaryFreeTextValid ||
    !differentialFreeTextValid ||
    !priorGeneticFreeTextValid ||
    overCombinedLimit
  const doneDisabled = isSaving || finalizeDisabled

  // Guard against re-entrancy for save-and-close finalization. The Modal calls
  // its `onClose` on exit and `handleDone`/`handleClose` also call `onClose()`
  // themselves, so without this flag the trailing close would fire `onDone`
  // twice and re-trigger HPO extraction. Reset each time the modal opens.
  const didFinalizeRef = useRef(false)

  useEffect(() => {
    if (isOpen) {
      didFinalizeRef.current = false
    }
  }, [isOpen])

  const finalize = () => {
    // Mirror the "Done" CTA's disabled guard: only finalize (persist + re-run
    // HPO extraction via `onDone`) when the content is valid. Invalid or
    // over-limit text should still close, but must not be sent for extraction —
    // the same reason the Done button is disabled in that state. Autosave has
    // already persisted the in-progress text either way.
    if (onDone && !didFinalizeRef.current && !finalizeDisabled) {
      didFinalizeRef.current = true
      onDone(data)
    }
    onClose()
  }

  const handleDone = finalize

  const handleContinue = async () => {
    if (!onContinue) return
    setIsContinuing(true)
    try {
      // Await so async work (HPO extraction) completes before the parent opens
      // the next modal — otherwise it hydrates with stale phenotypes.
      await onContinue(data)
    } finally {
      setIsContinuing(false)
    }
  }

  // Save & Close (escape key or the header close button) must behave like the
  // "Done" CTA: persist the edited summary AND re-run HPO extraction. Escape/X
  // previously bypassed `onDone`, so phenotype extraction never ran on
  // save-and-close — only on the explicit Done click.
  const handleClose = () => {
    // While `onContinue` is awaiting async extraction, escape/X must be inert.
    // `handleContinue` already keeps the modal open until extraction resolves
    // and the parent then opens Indications; letting close fire here would call
    // `onDone` (aborting the in-flight extraction) while the parent still
    // advances, so the next modal would hydrate from stale phenotypes.
    if (isContinuing) return
    finalize()
  }

  const handleDiscard = () => {
    // Discard must never finalize: block any trailing `onClose` from routing
    // through `onDone` and re-extracting the reverted summary.
    didFinalizeRef.current = true
    onSave(openBaselineRef.current)
    onClose()
  }

  // Toggling the section off drops any captured text in both fields so the
  // hidden values are never persisted or sent for phenotype extraction.
  const handleIncludeDifferentialChange = (next: boolean) => {
    setIncludeDifferential(next)
    if (!next) {
      setData((prev) => ({ ...prev, differentialDiagnosis: '', priorGeneticTesting: '' }))
    }
  }

  const clinicalTextareaClassName = `min-h-[204px] resize-y border-border-base-medium bg-background-secondary-medium py-3.5 shadow-xs dark:border-gray-600 dark:bg-gray-800/80 ${
    isSaving ? 'border-brand-600 focus:border-brand-600' : ''
  }`

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Clinical Summary"
      size="lg"
      footer={
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <kbd className="rounded-sm border border-border-base-medium bg-gray-100 px-2 py-1.5 font-sans text-xs font-normal text-text-body dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300">
              esc
            </kbd>
            <span className="text-xs text-text-body-subtle dark:text-gray-400">Save & Close</span>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end sm:gap-4">
            <SaveIndicator lastSaveTime={lastSaveTime} isSaving={isSaving} />
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <button
                type="button"
                disabled={isContinuing}
                onClick={handleDiscard}
                className="rounded-full border border-border-base bg-white px-4 py-2.5 text-sm font-medium text-text-heading shadow-xs transition-colors hover:bg-background-gray disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-900 dark:text-white dark:hover:bg-gray-800"
              >
                Discard Changes
              </button>
              {!onContinue && (
                <button
                  type="button"
                  disabled={doneDisabled || isContinuing}
                  onClick={onDone ? handleDone : handleClose}
                  className="rounded-full bg-brand-600 px-4 py-2.5 text-sm font-medium text-white shadow-xs transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Done
                </button>
              )}
              {onContinue && (
                <button
                  type="button"
                  disabled={doneDisabled || isContinuing}
                  onClick={handleContinue}
                  className="inline-flex items-center gap-2 rounded-full bg-brand-600 px-4 py-2.5 text-sm font-medium text-white shadow-xs transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isContinuing ? 'Extracting phenotypes…' : 'Continue to Indications for Testing'}
                  {!isContinuing && <ArrowRight className="h-4 w-4 shrink-0" aria-hidden />}
                </button>
              )}
            </div>
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-6">
        {showSummaryTip && (
          <AlertBanner useInflectionIcon={true} onClose={() => setShowSummaryTip(false)}>
            A detailed summary and thorough phenotype terms enhance diagnostic accuracy.
          </AlertBanner>
        )}

        {overCombinedLimit && (
          <AlertBanner variant="error">
            {isShared ? (
              SHARED_CLINICAL_TEXT_TOO_LONG_MESSAGE
            ) : (
              <>
                The combined clinical summary, differential diagnosis, prior genetic testing, and
                family history exceed the {CLINICAL_DESCRIPTION_MAX_LENGTH.toLocaleString()}-character
                limit. Please shorten your entries before continuing.
              </>
            )}
          </AlertBanner>
        )}

        {onAutoUpdatePhenotypesChange ? (
          <div className="rounded-xl border border-border-base bg-white p-4 shadow-xs dark:border-gray-700 dark:bg-gray-900">
            <Toggle
              size="panel"
              label="Automatically update Indications for Testing"
              description="Re-extract Human Phenotype Ontology (HPO) terms from the Clinical Summary when changes are saved"
              checked={Boolean(autoUpdatePhenotypes)}
              onChange={onAutoUpdatePhenotypesChange}
            />
          </div>
        ) : null}

        <TextareaInput
          ref={textareaRef}
          value={data.summary}
          onChange={(value) => setData({ ...data, summary: value })}
          placeholder="Enter clinical summary..."
          rows={8}
          className={clinicalTextareaClassName}
          highlightInvalidChars
          helperText={
            isShared
              ? SHARED_CLINICAL_TEXT_HELPER
              : 'The counter reflects the combined clinical summary, differential diagnoses, and family history.'
          }
          {...counterProps}
        />

        <div className="rounded-xl border border-border-base bg-white p-4 shadow-xs dark:border-gray-700 dark:bg-gray-900">
          <Toggle
            size="panel"
            label="Include Differential Diagnosis & Prior Genetic Testing"
            description="Optional. Provide any specific genetic conditions being considered for diagnosis and any previous genetic testing results. Phenotype terms will not be extracted from these fields."
            checked={includeDifferential}
            onChange={handleIncludeDifferentialChange}
          />
        </div>

        {includeDifferential ? (
          <>
            <TextareaInput
              value={data.differentialDiagnosis ?? ''}
              onChange={(value) => setData({ ...data, differentialDiagnosis: value })}
              label="Differential Diagnosis"
              placeholder="Optional: Please provide any specific genetic conditions being considered as a diagnosis for this patient."
              rows={8}
              className={clinicalTextareaClassName}
              highlightInvalidChars
              helperText={`Phenotype terms will not be extracted from the Differential Diagnosis. ${sectionCounterHelper}`}
              {...counterProps}
            />

            <TextareaInput
              value={data.priorGeneticTesting ?? ''}
              onChange={(value) => setData({ ...data, priorGeneticTesting: value })}
              label="Prior Genetic Testing"
              placeholder={`Optional: Please provide any previous genetic findings for this patient.

Please include the gene(s) and variant(s). For example:  ABC1 c.123C>T (p.Arg345Tyr) or Chr15:123456-1234789.`}
              rows={8}
              className={clinicalTextareaClassName}
              highlightInvalidChars
              helperText={`Phenotype terms will not be extracted from Prior Genetic Testing. ${sectionCounterHelper}`}
              {...counterProps}
            />
          </>
        ) : null}
      </div>
    </Modal>
  )
}
